import { LC_SHOES_PLACEHOLDER_IMAGE_ID, LC_SHOES_PLACEHOLDER_IMAGES, LC_SWATCH_IMAGE_ZOOM_IN_SUFFIX } from 'constants/launchCalendar';
import type { Product } from 'constants/searchTypes';
import type { ProductBundle } from 'types/cloudCatalog';
import type {
  LaunchCalendarCardStyleInfo,
  LaunchCalendarProductInfo,
  LaunchCalendarRecentLaunches,
  LaunchCalendarUpcomingStyles
} from 'types/launchCalendar';

/**
 *  As per info from Merch and BOS teams, hard launch date is the only Zappos platform in Eastern time.
 *  To support correct hard launch dates in EST the backend query computes the hard launch date from Unix epoch in Eastern time
 *  to safeguard against any unintended corner cases arising on the days of time zone switch due to Daylight Savings.
 *  Once the date is computed, it can be used as is in Pacific time.
 */
export const formatEpochToESTDate = ({ epoch }: { epoch: number }) => {
  // Create a Date object for the epoch time (epoch is in milliseconds)
  const date = new Date(epoch);

  // Use the Intl.DateTimeFormat API to set the time zone to EST (America/New_York)
  const estFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric'
  });

  // Format the date in EST
  return estFormatter.format(date);
};

/*
 * For the styles showing today's and upcoming launches on Release calendar page, we are using PST timezone to match the date
 */
export const isEpochTimeInESTEqualToCurrentDateInPST = ({ epochTimeInMilliSeconds }: { epochTimeInMilliSeconds: number }) => {
  // Create a Date object for the current date in the local time zone
  const currentDate = new Date();

  // Create a Date object for the epoch time (in milliseconds)
  const epochDate = new Date(epochTimeInMilliSeconds);

  // Use the Intl.DateTimeFormat API to set the time zone to PST (America/Los_Angeles)
  const pstFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });

  // Use the Intl.DateTimeFormat API to set the time zone to EST (America/New_York)
  const estFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });

  // Format the current date and epoch date in PST
  const currentDateInPST = pstFormatter.format(currentDate);
  const epochDateInEST = estFormatter.format(epochDate);

  // Check if the two formatted dates are equal
  return currentDateInPST === epochDateInEST;
};

export function getLaunchesData(upcomingStyles: LaunchCalendarUpcomingStyles) {
  let upcomingLaunchesTemp: LaunchCalendarCardStyleInfo[] = [];
  let todayLaunchesTemp: LaunchCalendarCardStyleInfo[] = [];

  Object.keys(upcomingStyles).forEach((launchTime: string) => {
    if (isEpochTimeInESTEqualToCurrentDateInPST({ epochTimeInMilliSeconds: Number(launchTime) })) {
      todayLaunchesTemp = todayLaunchesTemp.concat(upcomingStyles[launchTime]!.flatMap(item => item.relatedStyles));
    } else {
      if (Number(launchTime) < Date.now()) return;
      upcomingLaunchesTemp = upcomingLaunchesTemp.concat(upcomingStyles[launchTime]!.flatMap(item => item.relatedStyles));
    }
  });

  return {
    upcomingLaunches: upcomingLaunchesTemp,
    todayLaunches: todayLaunchesTemp
  };
}

export function getLCUpcomingProductInfo(upcomingStyles: LaunchCalendarUpcomingStyles) {
  let upcomingLaunchesTemp: LaunchCalendarProductInfo[] = [];
  let todayLaunchesTemp: LaunchCalendarProductInfo[] = [];

  Object.keys(upcomingStyles).forEach((launchTime: string) => {
    if (isEpochTimeInESTEqualToCurrentDateInPST({ epochTimeInMilliSeconds: Number(launchTime) })) {
      todayLaunchesTemp = todayLaunchesTemp.concat(upcomingStyles[launchTime]!);
    } else {
      if (Number(launchTime) < Date.now()) return;
      upcomingLaunchesTemp = upcomingLaunchesTemp.concat(upcomingStyles[launchTime]!);
    }
  });

  return {
    upcomingLaunches: upcomingLaunchesTemp,
    todayLaunches: todayLaunchesTemp
  };
}

export function getLCRecentLaunchesInfo(recentLaunches: LaunchCalendarRecentLaunches) {
  let recentlyLaunched: LaunchCalendarProductInfo[] = [];

  Object.keys(recentLaunches).forEach((launchTime: string) => {
    if (!isEpochTimeInESTEqualToCurrentDateInPST({ epochTimeInMilliSeconds: Number(launchTime) }) && Number(launchTime) < Date.now()) {
      recentlyLaunched = recentlyLaunched.concat(recentLaunches[launchTime]!);
    }
  });

  return recentlyLaunched;
}

/**
 * Helper function to translate LaunchCalendarCardStyleInfo[] to Products
 * Needed for mapping related styles in ColorSwatches
 * @param relatedStyles
 */
export const translateToProducts = (relatedStyles: LaunchCalendarCardStyleInfo[]): Product[] =>
  relatedStyles.map(
    (value: LaunchCalendarCardStyleInfo): Product => ({
      ...value,

      imageMap: {
        SWCH: value.imageId
          ? `${value.imageId}${LC_SWATCH_IMAGE_ZOOM_IN_SUFFIX}`
          : `${LC_SHOES_PLACEHOLDER_IMAGE_ID}${LC_SWATCH_IMAGE_ZOOM_IN_SUFFIX}`
      },
      imageId: value.imageId || `${LC_SHOES_PLACEHOLDER_IMAGE_ID}`,
      isCouture: false,
      isNew: '',
      onHand: 0,
      animationImages: [],
      brandName: value.brandName,
      colorId: value.colorId,
      onSale: '',
      originalPrice: '',
      percentOff: '',
      price: '',
      productId: String(value.productId),
      productName: value.productName,
      productRating: 0,
      productSeoUrl: '',
      productUrl: `/product/${value.productId}/color/${value.colorId}`,
      reviewCount: 0,
      reviewRating: 0,
      styleColor: value.colorName,
      styleId: String(value.styleId),
      swatchUrl: '',
      color: value.colorName,
      sizing: {}
    })
  );

export const addPlaceHolderImageInResponse = (productBundle: ProductBundle): ProductBundle => {
  const updatedStyles = productBundle.styles.map(style => {
    const updatedImages = Array.isArray(style.images) && style.images.length > 0 ? style.images : LC_SHOES_PLACEHOLDER_IMAGES;
    return { ...style, images: updatedImages };
  });

  return {
    ...productBundle,
    styles: updatedStyles
  };
};
